import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
// ============= AI Verification Types (Issue #411) =============
/**
 * Type of verification question the AI can ask
 * Each question type maps to a specific code lookup operation
 */
export const VerificationQuestionType = z.enum([
    'find-prop-source', // "Where does prop X come from?" - traces callback props to their source
    'find-state-setter', // "What state does function Y modify?" - finds state mutations in a function
    'check-persistence', // "Is state X persisted anywhere?" - searches for localStorage/API persistence
    'find-function-body', // "Show me the body of function Z" - retrieves full function implementation
    'find-component-usage', // "Where is component X used?" - finds JSX usages of a component
    'check-context-usage', // "Is state X from React Context?" - detects React Context state
]);
/**
 * Parameters for a verification question
 */
export const VerificationQuestionParamsSchema = z.object({
    /** Component name for component-related questions */
    componentName: z.string().optional(),
    /** Prop name to trace */
    propName: z.string().optional(),
    /** Function name to analyze */
    functionName: z.string().optional(),
    /** State variable name */
    stateName: z.string().optional(),
    /** File path for context */
    filePath: z.string().optional(),
    /** Handler function name */
    handlerName: z.string().optional(),
});
/**
 * AI verification question - asked by AI to verify an inference
 */
export const AIVerificationQuestionSchema = z.object({
    /** Unique question identifier */
    questionId: z.string(),
    /** Type of question */
    questionType: VerificationQuestionType,
    /** Question-specific parameters */
    params: VerificationQuestionParamsSchema,
    /** What the AI is trying to verify */
    hypothesis: z.string(),
    /** AI's confidence before verification (0-1) */
    confidence: z.number().min(0).max(1),
});
/**
 * Code search result from verification
 */
export const CodeSearchResultSchema = z.object({
    /** File where result was found */
    file: z.string(),
    /** Line number */
    line: z.number(),
    /** Line end (for multi-line results) */
    lineEnd: z.number().optional(),
    /** Content snippet */
    content: z.string(),
    /** Component name (if applicable) */
    componentName: z.string().optional(),
    /** Function name (if applicable) */
    functionName: z.string().optional(),
    /** State setters found (if applicable) */
    stateSetters: z.array(z.string()).optional(),
    /** State definitions found (if applicable) */
    stateDefinitions: z.array(z.object({
        name: z.string(),
        type: z.enum(['useState', 'useReducer', 'context', 'redux', 'zustand', 'other']),
        file: z.string(),
        line: z.number(),
    })).optional(),
});
/**
 * Answer to a verification question
 */
export const AIVerificationAnswerSchema = z.object({
    /** Question ID this answers */
    questionId: z.string(),
    /** Whether the verification found relevant results */
    found: z.boolean(),
    /** Search results */
    results: z.array(CodeSearchResultSchema),
    /** Human-readable summary of findings */
    summary: z.string(),
    /** Updated confidence based on verification */
    updatedConfidence: z.number().min(0).max(1).optional(),
});
// ============= Prop Callback Resolution Types =============
/**
 * State definition type
 */
export const StateDefinitionTypeSchema = z.enum([
    'useState',
    'useReducer',
    'context',
    'redux',
    'zustand',
    'other',
]);
/**
 * State definition information
 */
export const StateDefinitionSchema = z.object({
    /** State variable name */
    name: z.string(),
    /** Type of state management */
    type: StateDefinitionTypeSchema,
    /** File where state is defined */
    file: z.string(),
    /** Line number of definition */
    line: z.number(),
    /** Initial value (if detectable) */
    initialValue: z.string().optional(),
    /** Whether state is persisted */
    isPersisted: z.boolean().optional(),
    /** Persistence mechanism (if persisted) */
    persistenceMechanism: z.string().optional(),
});
/**
 * Resolution of a prop callback to its source
 */
export const PropCallbackResolutionSchema = z.object({
    /** The prop name that was resolved */
    propName: z.string(),
    /** Child component receiving the prop */
    childComponent: z.string(),
    /** File containing child component usage */
    childFile: z.string(),
    /** Parent component passing the prop */
    parentComponent: z.string(),
    /** File containing parent component */
    parentFile: z.string(),
    /** Line in parent where prop is passed */
    parentLine: z.number(),
    /** The actual handler function name */
    actualHandler: z.string(),
    /** Line where handler is defined */
    handlerLine: z.number(),
    /** State setters called by the handler */
    stateSetters: z.array(z.string()),
    /** State definitions for the setters */
    stateDefinitions: z.array(StateDefinitionSchema),
    /** Code evidence for the resolution */
    evidence: CodeEvidenceSchema,
    /** Whether the prop is passed through multiple levels */
    isPropDrilling: z.boolean().optional(),
    /** Intermediate components if prop drilling */
    intermediateComponents: z.array(z.string()).optional(),
});
// ============= Enhanced Enhancement Types =============
/**
 * Phase 1 AI response - includes verification questions
 */
export const Phase1EnhancementResponseSchema = z.object({
    /** Suggested flow name */
    suggestedName: z.string().optional(),
    /** Suggested flow description */
    suggestedDescription: z.string().optional(),
    /** Step enhancements */
    stepEnhancements: z.array(z.object({
        stepId: z.string(),
        suggestedLabel: z.string(),
        suggestedDescription: z.string().optional(),
        reasoning: z.string(),
        confidence: z.number(),
    })),
    /** Missing steps (preliminary - may be revised after verification) */
    missingSteps: z.array(z.object({
        afterStepId: z.string(),
        suggestedType: z.string(),
        suggestedName: z.string(),
        description: z.string(),
        reasoning: z.string(),
        confidence: z.number(),
        /** Whether this needs verification */
        needsVerification: z.boolean().optional(),
    })),
    /** Completeness assessment (preliminary) */
    completeness: z.object({
        score: z.number(),
        reasoning: z.string(),
        suggestions: z.array(z.string()),
        gaps: z.array(z.string()).optional(),
    }),
    /** Verification questions to answer before finalizing */
    verificationQuestions: z.array(AIVerificationQuestionSchema).optional(),
});
/**
 * Verification result summary
 */
export const VerificationResultSummarySchema = z.object({
    /** Total questions asked */
    totalQuestions: z.number(),
    /** Questions with positive findings */
    foundCount: z.number(),
    /** Questions with negative findings */
    notFoundCount: z.number(),
    /** Whether any inferences were revised */
    revisionsNeeded: z.boolean(),
    /** Summary of key findings */
    keyFindings: z.array(z.string()),
});
/**
 * Complete verification context for Phase 2
 */
export const VerificationContextSchema = z.object({
    /** Original Phase 1 response */
    phase1Response: Phase1EnhancementResponseSchema,
    /** Questions that were asked */
    questions: z.array(AIVerificationQuestionSchema),
    /** Answers received */
    answers: z.array(AIVerificationAnswerSchema),
    /** Summary of verification results */
    summary: VerificationResultSummarySchema,
});
// ============= Evidence Level Enhancement =============
/**
 * Enhanced evidence level with verification status
 */
export const VerifiedEvidenceLevelSchema = z.enum([
    'ast-verified', // Verified through AST prop tracing
    'ai-verified', // Verified through AI verification loop
    'ai-inferred', // AI inference without verification
    'partial', // Some evidence but not fully verified
]);
/**
 * Verification trail for an enhancement
 */
export const VerificationTrailSchema = z.object({
    /** Evidence level */
    level: VerifiedEvidenceLevelSchema,
    /** How the evidence was obtained */
    method: z.enum(['ast-prop-trace', 'ai-verification', 'pattern-match', 'inference']),
    /** Questions asked (if AI verification) */
    questionsAsked: z.array(z.string()).optional(),
    /** Key findings from verification */
    findings: z.array(z.string()).optional(),
    /** Whether inference was revised after verification */
    wasRevised: z.boolean().optional(),
    /** Original inference before revision */
    originalInference: z.string().optional(),
});
//# sourceMappingURL=verification.js.map